import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Wind } from "@phosphor-icons/react";

const PHASES = [
  { label: "Breathe in", secs: 4, scale: 1 },
  { label: "Hold", secs: 4, scale: 1 },
  { label: "Breathe out", secs: 4, scale: 0.55 },
  { label: "Hold", secs: 4, scale: 0.55 },
];
const TOTAL_CYCLES = 8; // ≈ 2 minutes

export default function BreathingTool({ open, onOpenChange }) {
  const [phase, setPhase] = useState(0);
  const [cycle, setCycle] = useState(0);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    if (!open) { setPhase(0); setCycle(0); setFinished(false); return; }
    if (finished) return;
    const t = setTimeout(() => {
      const next = (phase + 1) % PHASES.length;
      if (next === 0) {
        if (cycle + 1 >= TOTAL_CYCLES) { setFinished(true); return; }
        setCycle((c) => c + 1);
      }
      setPhase(next);
    }, PHASES[phase].secs * 1000);
    return () => clearTimeout(t);
  }, [open, phase, cycle, finished]);

  const p = PHASES[phase];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm rounded-3xl p-8 text-center" data-testid="breathing-dialog">
        <div className="flex items-center justify-center gap-2">
          <Wind size={18} weight="fill" color="var(--brand)" />
          <DialogTitle className="label-eyebrow font-normal">2-minute box breathing</DialogTitle>
        </div>

        {finished ? (
          <div className="py-10" data-testid="breathing-finished">
            <p className="font-display text-2xl font-light">Nice reset 🌿</p>
            <p className="text-sm text-muted2 mt-2">Your nervous system thanks you. Carry that calm with you.</p>
            <button data-testid="breathing-close-btn" onClick={() => onOpenChange(false)}
              className="mt-6 bg-ink text-bg rounded-full px-6 py-2.5 text-sm font-medium hover:bg-brand transition-colors">
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="relative h-56 flex items-center justify-center">
              <motion.div
                className="absolute w-44 h-44 rounded-full bg-brand/10 border border-brand/25"
                animate={{ scale: p.scale }}
                transition={{ duration: p.secs, ease: "easeInOut" }}
              />
              <motion.div
                className="absolute w-28 h-28 rounded-full bg-brand/20"
                animate={{ scale: p.scale }}
                transition={{ duration: p.secs, ease: "easeInOut" }}
              />
              <div className="relative">
                <p data-testid="breathing-phase" className="font-display text-xl font-medium">{p.label}</p>
                <p className="text-xs text-muted2 mt-1">{p.secs} seconds</p>
              </div>
            </div>
            <div className="flex items-center justify-center gap-1.5 mt-2">
              {Array.from({ length: TOTAL_CYCLES }).map((_, i) => (
                <span key={i} className={`w-1.5 h-1.5 rounded-full ${i <= cycle ? "bg-brand" : "bg-border"}`} />
              ))}
            </div>
            <button data-testid="breathing-end-btn" onClick={() => onOpenChange(false)}
              className="mt-5 text-sm text-muted2 hover:text-ink transition-colors">
              End early
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
